/**
 * Backend origin used by REST calls (the Reports v2 client).
 *
 * The GraphQL client keeps its own URI in src/graphql/apollo-client.js, which
 * is hard-coded to http://localhost:3000/graphql for local work. This helper
 * reproduces that resolution for REST so both halves of the app talk to the
 * SAME backend.
 *
 * Resolution order:
 *   1. REACT_APP_REPORTS_API_URL      — explicit override, wins everywhere.
 *   2. Served from localhost/127.0.0.1 — a local REACT_APP_GRAPHQL_URI if one is
 *      configured (e.g. :5000), otherwise http://localhost:3000.
 *   3. REACT_APP_GRAPHQL_URI          — /graphql suffix stripped (deployed).
 *
 * Why this exists: dashboard/.env sets
 * `REACT_APP_GRAPHQL_URI=https://api.shalibowellness.com/graphql` (production).
 * Reading it verbatim while the app runs on localhost:3001 sent
 * POST /api/v2/reports/detailed to PRODUCTION, which has no v2 report routes —
 * every "Generate detailed report" click failed with a 404 while GraphQL
 * queries happily hit the local backend.
 */

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0", "[::1]"];

/** Drop a trailing /graphql (with or without trailing slash) and any trailing slash. */
export function toOrigin(uri) {
  return String(uri || "")
    .trim()
    .replace(/\/graphql\/?$/i, "")
    .replace(/\/+$/, "");
}

function isLocalOrigin(origin) {
  try {
    const { hostname } = new URL(origin);
    return LOCAL_HOSTNAMES.includes(hostname);
  } catch (e) {
    return false;
  }
}

export function isLocalPage() {
  if (typeof window === "undefined" || !window.location) return false;
  return LOCAL_HOSTNAMES.includes(window.location.hostname);
}

export function getApiBaseUrl() {
  const override = toOrigin(process.env.REACT_APP_REPORTS_API_URL);
  if (override) return override;

  const envOrigin = toOrigin(process.env.REACT_APP_GRAPHQL_URI);

  if (isLocalPage()) {
    // Local development: prefer an explicitly configured *local* backend,
    // otherwise the local one the GraphQL client uses.
    if (envOrigin && isLocalOrigin(envOrigin)) return envOrigin;
    return "https://api.shalibowellness.com";
  }

  return envOrigin || "https://api.shalibowellness.com";
}

export default getApiBaseUrl;
